import { useEffect, useRef } from 'react';
import { keepPickedFolder } from 'helpers/picked-folder';
import { pickedFromInput, toImportFiles } from 'helpers/upload-selection';
import { goToPage, RouteName } from 'routes';

interface PromptFileChooserProps {
  vaultId: number;
  // The album the prompt is in; none for a prompt in the hidden "Without
  // Album" one, whose file stays unsorted.
  albumId?: number;
  // The prompt (an album content id) this file answers.
  promptId: number;
  onClose: () => void;
}

// "Upload" on a prompt: the browser's own file dialog, for one file. (The
// Upload modal's folders and cloud sources pick many.) The file goes to the
// import review, which removes the prompt once the memory is imported.
export const PromptFileChooser = ({
  vaultId,
  albumId,
  promptId,
  onClose,
}: PromptFileChooserProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // The host's onClose changes every render; the dialog must open only once.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Opens as soon as it's shown, from the click that picked "Upload". Closing
  // the dialog without a file ends it too.
  useEffect(() => {
    const input = inputRef.current;
    const onCancel = () => closeRef.current();
    input?.addEventListener('cancel', onCancel);
    input?.click();
    return () => input?.removeEventListener('cancel', onCancel);
  }, []);

  const onPick = (file?: File) => {
    onClose();
    if (!file) return;
    const [picked] = toImportFiles(pickedFromInput([file]));
    if (!picked) return;
    goToPage(
      RouteName.VaultImport,
      { id: vaultId },
      {
        source: 'computer',
        pick: keepPickedFolder([picked], null),
        ...(albumId ? { album: albumId } : {}),
        prompt: promptId,
      },
    );
  };

  return (
    <input
      ref={inputRef}
      type="file"
      hidden
      onChange={(e) => {
        const file = e.target.files?.[0];
        // Clear so picking the same file again still fires onChange.
        e.target.value = '';
        onPick(file);
      }}
    />
  );
};
